import React, {Component, createRef} from 'react';

// CSS
import './Exams.scss';

// Helper
import {checkCookie} from '../../Helper/Cookies';
import { apiFetch, getCurrentUser } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Panel, Confirm } from '../../components/UI/Overlays';
import { EmptyState, TableWait, Chip, StatTile, Tip } from '../../components/UI/Feedback';
import { Toolbar } from '../../components/UI/Controls';
import AddExam from './AddExam';
import EditExam from './EditExam';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';

// Properties
const properties = require('../../properties.json');

class AllExams extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        globalFilter: null,

        // Teacher Identifier
        branchID: getCurrentUser()?.branchId,
        companyID: properties.company_id,
        // Data
        data: [],
        stagesById: {},
        lessonsById: {},

        // Update Data
        resend: false,

        // ---- the page's own states ----
        tableLoading: true,

        // adding and editing happen over the list now; the two pages they
        // mount are the same ones /exams/add and /exams/edit/:id mount
        addOpen: false,
        editId: null,

        // the exam the teacher asked to delete, held while the card is up
        doomed: null,
        deleting: false,
    }

    getExams = () => {
        var _this = this;
        // Get Exams From API
        this.setState({ tableLoading: true });
        apiFetch("/exams")
        .then(fetchExams => {
            this.setState({
                data: fetchExams,
                tableLoading: false
            });
        })
        .catch(error => {
            console.error('Error:', error)

            this.setState({ tableLoading: false });

            _this.toast.current.show({severity: 'error', summary: 'لم تتم العملية', detail: 'خطأ في الإتصال'});
        });
    }

    // The question is asked in the product's own card; everything after
    // "yes" is the call it always was.
    deleteExam = (id) => {
        var _this = this;

        this.setState({ deleting: true });

        // Delete Subscription By ID
        apiFetch(`/exams/${id}`, { method: "DELETE" })
        .then(() => {
            this.setState({resend: true, doomed: null, deleting: false});
            _this.toast.current.show({severity: 'success', summary: 'تمت العملية', detail: 'تم الحذف بنجاح'});
        })
        .catch(error => {
            console.error('Error:', error)
            this.setState({doomed: null, deleting: false});
            _this.toast.current.show({severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال'});
        });
    }

    componentDidMount () {
        document.title = properties.site_name + " | كل الإمتحانات";
        // Redirect To Sign In Page If The User Is Already Logged Out & Remove The Data If It Exists
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
        }

        this.getExams();

        apiFetch("/lookup/school-stages/by-teacher", { query: { teacherUserId: getCurrentUser()?.userId } })
        .then(stages => {
            const stagesById = {};
            stages.forEach(s => { stagesById[s.id] = s.name; });
            this.setState({ stagesById });
        })
        .catch(error => console.error('Error:', error));

        apiFetch("/lessons")
        .then(lessons => {
            const lessonsById = {};
            lessons.forEach(l => { lessonsById[l.id] = l.lessonName; });
            this.setState({ lessonsById });
        })
        .catch(error => console.error('Error:', error));
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevState.resend !== this.state.resend) {
            this.getExams();

            this.setState({resend: false});
        }
    }

    render() {
        const { data, tableLoading, doomed, deleting, addOpen, editId } = this.state;

        // the add and edit pages, mounted over the list instead of being
        // navigated to -- both still work on their own routes
        const openAdd = () => this.setState({ addOpen: true });
        const closeAdd = () => {
            this.setState({ addOpen: false });
            this.getExams();
        };
        const openEdit = (id) => this.setState({ editId: id });
        const closeEdit = () => {
            this.setState({ editId: null });
            this.getExams();
        };

        const live = data.filter((e) => Number(e.active) === 1).length;
        const marks = data.reduce((sum, e) => sum + Number(e.degree || 0), 0);

        const actionBodyTemplate = (rowData) => {
            return (
                <React.Fragment>
                    <Tip text="تعديل">
                        <Button icon="pi pi-pencil" className="p-button-rounded p-button-secondary p-ml-2" onClick={() => openEdit(rowData.id)} />
                    </Tip>
                    <Tip text="حذف">
                        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => this.setState({ doomed: rowData })} />
                    </Tip>
                </React.Fragment>
            );
        }

        // the exam as the teacher set it: its name, and the lesson it belongs
        // to underneath
        const nameBody = (row) => (
            <span className="ui-stack">
                <b>{row.examName}</b>
                <small>{this.state.lessonsById[row.lessonId] || 'مش مربوط بدرس'}</small>
            </span>
        );

        const degreeBody = (row) => (
            <span className="ui-money">
                <b>{Number(row.degree || 0)}</b>
                <small>درجة</small>
            </span>
        );

        const timerBody = (row) =>
            Number(row.timer) ? (
                <span className="ui-money">
                    <b>{row.timer}</b>
                    <small>دقيقة</small>
                </span>
            ) : (
                <Chip kind="off">من غير وقت</Chip>
            );

        // two facts that decide whether a student sees it, and how it is
        // marked
        const stateBody = (row) => (
            <span className="ui-bits">
                {Number(row.active) === 1 ? <Chip kind="ok">مفعّل</Chip> : <Chip kind="off">غير مفعّل</Chip>}
                <Chip kind="wait">{Number(row.examType) === 1 ? 'تصحيح يدوي' : 'تصحيح آلي'}</Chip>
            </span>
        );

        return (
            <>
                <Header
                    pageName="كل الإمتحانات"
                    action={{ label: "إضافة إمتحان", icon: "pi pi-plus", onClick: openAdd }}
                />

                <Toast ref={this.toast}></Toast>

                {/* the add page, mounted over the list */}
                <Panel
                    wide
                    visible={addOpen}
                    onHide={closeAdd}
                    title="إمتحان جديد"
                    subtitle="بياناته، وأسئلته وإجاباتها"
                >
                    {addOpen ? <AddExam inPanel onSaved={closeAdd} /> : null}
                </Panel>

                <Panel
                    wide
                    visible={!!editId}
                    onHide={closeEdit}
                    title="تعديل الإمتحان"
                    subtitle="غيّر بياناته أو أسئلته"
                >
                    {editId ? <EditExam inPanel examId={editId} onSaved={closeEdit} /> : null}
                </Panel>

                <Confirm
                    visible={!!doomed}
                    onHide={() => this.setState({ doomed: null })}
                    name={doomed ? doomed.examName : ''}
                    note="الإمتحان هيتشال بكل أسئلته وإجاباته، ومش هينفع ترجع عنه."
                    busy={deleting}
                    onConfirm={() => this.deleteExam(doomed.id)}
                />

                <div className="content">
                    <div className="container">
                        <div className="card">
                            <Card>
                                <div className="ui-tiles">
                                    <StatTile
                                        brand
                                        icon="pi pi-list"
                                        label="إمتحانات"
                                        value={tableLoading ? '…' : data.length}
                                    />
                                    <StatTile
                                        icon="pi pi-check-circle"
                                        tone="green"
                                        label="مفعّلة للطلاب"
                                        value={tableLoading ? '…' : live}
                                    />
                                    <StatTile
                                        icon="pi pi-star"
                                        tone="gold"
                                        label="مجموع الدرجات"
                                        value={tableLoading ? '…' : marks}
                                    />
                                </div>

                                <Toolbar
                                    value={this.state.globalFilter || ''}
                                    onSearch={(v) => this.setState({ globalFilter: v })}
                                    placeholder="بحث باسم الإمتحان"
                                    count={tableLoading ? null : `${data.length} إمتحان`}
                                />

                                <DataTable sortable globalFilter={this.state.globalFilter} className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped" value={data} paginator rows={10} rowsPerPageOptions={[10,20,50]} currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                                emptyMessage={
                                    tableLoading ? (
                                        <TableWait />
                                    ) : (
                                        <EmptyState
                                            title="لسه مفيش إمتحانات"
                                            hint="ابدأ بإمتحان واحد على درس، وحدّد أسئلته ودرجاته"
                                            icon="pi pi-list"
                                            action={{ label: "إضافة إمتحان", onClick: openAdd }}
                                        />
                                    )
                                }>
                                    <Column sortable field="examName" header="الإمتحان" body={nameBody}></Column>
                                    <Column
                                        sortable
                                        field="schoolStagesId"
                                        body={(row) => this.state.stagesById[row.schoolStagesId] || '—'}
                                        header="المرحلة الدراسية"
                                        style={{ width: '170px' }}
                                    ></Column>
                                    <Column sortable field="degree" header="الدرجة" body={degreeBody} style={{ width: '120px' }}></Column>
                                    <Column sortable field="timer" header="الوقت" body={timerBody} style={{ width: '130px' }}></Column>
                                    <Column sortable field="active" header="الحالة" body={stateBody} style={{ width: '230px' }}></Column>
                                    <Column header="تعديل / حذف" body={actionBodyTemplate}></Column>
                                </DataTable>
                            </Card>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default AllExams;
